import React, { useEffect, useState } from 'react';

import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  SortByAlpha as SortByAlphaIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import type { Transformer } from '@/types';
import { logger } from '@/utils/logger';

import {
  useDeleteTransformerMutation,
  useGetTransformersQuery,
  useLazyGetTransformerVersionsQuery,
  useUpdateTransformerInfoMutation,
} from '../../transformers/api/transformersApi';
import { PropertiesModal } from '../../transformers/components/PropertiesModal';
import { useRecipeEditor } from '../context/RecipeEditorContext';

interface RecipeListSidebarProps {
  recipeName?: string;
  onCollapseChange?: (collapsed: boolean) => void;
}

const SIDEBAR_WIDTH = 240;
const COLLAPSED_WIDTH = 40;

export const RecipeListSidebar: React.FC<RecipeListSidebarProps> = ({
  recipeName,
  onCollapseChange,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    recipe: Transformer | null;
  } | null>(null);
  const { appId, recipeId } = useParams();
  const navigate = useNavigate();
  const { state, dispatch } = useRecipeEditor();
  const { showSnackbar } = useSnackbar();
  const { isSwitchingRecipe, isSwitchingMode } = state;

  // Track the last recipe ID for UI purposes (e.g., showing spinner on the target item)
  const [lastRecipeId, setLastRecipeId] = useState<string | undefined>(recipeId);

  // Dialog states
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<Transformer | null>(null);

  const { data: recipes = [], isLoading } = useGetTransformersQuery(appId || '', {
    skip: !appId,
  });

  // Mutations
  const [deleteTransformer, { isLoading: isDeleting }] = useDeleteTransformerMutation();
  const [updateTransformerInfo, { isLoading: isRenaming }] = useUpdateTransformerInfoMutation();
  const [triggerGetVersions] = useLazyGetTransformerVersionsQuery();

  // Sync lastRecipeId when recipe ID changes (for UI spinner tracking only).
  // NOTE: isSwitchingRecipe is cleared by RecipeEditorContent AFTER the new
  // recipe data has been fully loaded and applied to the editor - NOT here.
  useEffect(() => {
    if (recipeId && recipeId !== lastRecipeId) {
      setLastRecipeId(recipeId);
    }
  }, [recipeId, lastRecipeId]);

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onCollapseChange?.(newState);
  };

  const handleContextMenu = (event: React.MouseEvent, recipe: Transformer) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            recipe,
          }
        : null
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // --- Rename ---
  const handleRename = () => {
    if (contextMenu?.recipe) {
      setSelectedRecipe(contextMenu.recipe);
      setRenameValue(contextMenu.recipe.name);
      setRenameDialogOpen(true);
    }
    handleCloseContextMenu();
  };

  const handleRenameConfirm = async () => {
    if (!selectedRecipe || !appId || !renameValue.trim()) return;

    try {
      // Fetch the latest version to get all required fields
      const { data: versions } = await triggerGetVersions({
        transBaseId: selectedRecipe.baseId,
        appId,
      });

      const currentVersion = versions?.[0];
      if (!currentVersion) {
        logger.error('Could not load recipe version data');
        return;
      }

      const newName = renameValue.trim().endsWith('.lr')
        ? renameValue.trim()
        : `${renameValue.trim()}.lr`;

      await updateTransformerInfo({
        appId,
        transId: selectedRecipe.baseId,
        data: {
          name: newName,
          config: currentVersion.config || '',
          tag: currentVersion.environ || '',
          version: Number(currentVersion.version),
          baseId: currentVersion.baseId,
          id: Number(currentVersion.id),
          description: currentVersion.description || '',
          languageVersion: currentVersion.languageVersion || '2.0',
          processingType: currentVersion.processingType || 'batch',
          maxDataProcTime: currentVersion.maxDataProcTime || '0001-01-01T00:00:00Z',
        },
      }).unwrap();

      setRenameDialogOpen(false);
      setSelectedRecipe(null);
      showSnackbar('Recipe renamed successfully', 'success');
    } catch (error) {
      logger.error('Failed to rename recipe:', error);
      showSnackbar('Failed to rename recipe', 'error');
    }
  };

  const handleRenameCancel = () => {
    setRenameDialogOpen(false);
    setSelectedRecipe(null);
  };

  // --- Properties ---
  const handleProperties = () => {
    if (contextMenu?.recipe) {
      setSelectedRecipe(contextMenu.recipe);
      setPropertiesModalOpen(true);
    }
    handleCloseContextMenu();
  };

  const handleCloseProperties = () => {
    setPropertiesModalOpen(false);
    setSelectedRecipe(null);
  };

  // --- Delete ---
  const handleDelete = () => {
    if (contextMenu?.recipe) {
      setSelectedRecipe(contextMenu.recipe);
      setDeleteDialogOpen(true);
    }
    handleCloseContextMenu();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRecipe) return;

    try {
      await deleteTransformer(selectedRecipe.baseId).unwrap();

      setDeleteDialogOpen(false);

      // If the deleted recipe is currently open, navigate to the recipes list
      if (String(selectedRecipe.baseId) === recipeId) {
        navigate(`/a/${appId}/tf`);
      }

      setSelectedRecipe(null);
      showSnackbar('Recipe deleted successfully', 'success');
    } catch (error) {
      logger.error('Failed to delete recipe:', error);
      showSnackbar('Failed to delete recipe', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRecipe(null);
  };

  // --- Execution History ---
  const handleExecutionHistory = () => {
    if (contextMenu?.recipe) {
      navigate(`/a/${appId}/health?recipe=${contextMenu.recipe.baseId}`);
    }
    handleCloseContextMenu();
  };

  // --- New Recipe ---
  const handleNewRecipeOpen = () => {
    // Navigate to the full-page recipe creation with template selection
    // (matches legacy dataProtect route: /a/:appId/tf/new)
    navigate(`/a/${appId}/tf/new`);
  };

  const handleRecipeClick = (recipe: Transformer) => {
    if (isSwitchingRecipe || isSwitchingMode) return; // Prevent clicks during switching
    if (String(recipe.baseId) === recipeId) return; // Already viewing this recipe

    // Set switching state before navigation
    dispatch({ type: 'SET_IS_SWITCHING_RECIPE', payload: true });
    setLastRecipeId(recipeId);
    navigate(`/a/${appId}/tf/${recipe.baseId}`);
  };

  const filteredRecipes = recipes
    .filter((recipe) => recipe.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .slice()
    .sort((a, b) => {
      const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      return sortOrder === 'asc' ? cmp : -cmp;
    });

  // Find current recipe name from list if not passed
  const currentRecipe = recipes.find((r) => String(r.baseId) === recipeId);
  const displayName = recipeName || currentRecipe?.name || 'Recipe Editor';

  return (
    <Box
      sx={{
        width: isCollapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        minWidth: isCollapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        height: '100%',
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease, min-width 0.2s ease',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {isCollapsed ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1 }}>
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton onClick={toggleCollapse} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <>
          {/* Current Recipe Name Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 44,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              sx={{ flex: 1, mr: 1 }}
              title={displayName}
            >
              {displayName}
            </Typography>
            <Tooltip title="Collapse sidebar">
              <IconButton onClick={toggleCollapse} size="small" edge="end">
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Recipe List Header */}
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="overline"
              fontWeight={700}
              letterSpacing="0.08em"
              color="text.primary"
            >
              RECIPES
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip
                title={
                  sortOrder === 'asc' ? 'Sorted A→Z (click for Z→A)' : 'Sorted Z→A (click for A→Z)'
                }
              >
                <IconButton
                  size="small"
                  onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  sx={{ width: 24, height: 24 }}
                >
                  <SortByAlphaIcon
                    sx={{
                      fontSize: 16,
                      color: sortOrder === 'desc' ? 'primary.main' : 'text.secondary',
                      transform: sortOrder === 'desc' ? 'scaleY(-1)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Create new recipe">
                <IconButton
                  size="small"
                  onClick={handleNewRecipeOpen}
                  color="primary"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    width: 24,
                    height: 24,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Search */}
          <Box sx={{ px: 1.5, pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search recipes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '13px',
                  '& input': {
                    py: 0.75,
                  },
                },
              }}
            />
          </Box>

          {/* Recipe List */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {/* eslint-disable-next-line no-nested-ternary -- Loading state logic naturally nests */}
            {isLoading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            ) : filteredRecipes.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? 'No recipes found' : 'No recipes yet'}
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {filteredRecipes.map((recipe) => {
                  const isSelected = String(recipe.baseId) === recipeId;
                  const isSwitchingToThis = isSwitchingRecipe && isSelected;

                  return (
                    <ListItem key={recipe.baseId} disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleRecipeClick(recipe)}
                        onContextMenu={(e) => handleContextMenu(e, recipe)}
                        disabled={isSwitchingRecipe || isSwitchingMode}
                        sx={{
                          py: 0.75,
                          px: 2,
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            '&:hover': {
                              bgcolor: 'action.selected',
                            },
                          },
                          '&.Mui-disabled': {
                            opacity: isSwitchingToThis ? 1 : 0.6,
                          },
                        }}
                      >
                        <ListItemText
                          primary={recipe.name}
                          primaryTypographyProps={{
                            variant: 'body2',
                            noWrap: true,
                            fontSize: '13px',
                          }}
                        />
                        {isSwitchingToThis && <CircularProgress size={16} sx={{ ml: 1 }} />}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </>
      )}

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
      >
        <MenuItem onClick={handleRename}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleProperties}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleExecutionHistory}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Execution History</ListItemText>
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={handleRenameCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Recipe</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Recipe Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRenameConfirm();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleRenameCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleRenameConfirm}
            variant="contained"
            disabled={isRenaming || !renameValue.trim()}
          >
            {isRenaming ? 'Renaming...' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedRecipe?.name && <>This will delete &quot;{selectedRecipe.name}&quot;</>}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleDeleteCancel}>
            Back
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Properties Modal */}
      <PropertiesModal
        open={propertiesModalOpen}
        recipe={selectedRecipe}
        appId={appId || ''}
        onClose={handleCloseProperties}
      />
    </Box>
  );
};

export default RecipeListSidebar;
